'use client'

import _ from 'lodash'
import { useState, useEffect } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { twMerge } from 'tailwind-merge'
import { useForm, Controller } from 'react-hook-form'
import { useMutation } from "@tanstack/react-query"
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useLocalStorageState } from 'ahooks'
import { Dialog, DialogPanel, DialogBackdrop, Radio, RadioGroup } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/20/solid'

import { Cruz } from '@cruz-com/lookup'
import { Button, Textarea } from '@/components'
import { useGlobalStore } from '@/store/provider'
import { contactApi } from '@/api'

const schema = z.object({
  rate: z.number().optional(),
  email: z.string().optional(),
  feedback: z.string().min(1, 'Feedback is required'),
})

export function FeedbackForm() {
  const { user, showFeedback, setShowFeedback } = useGlobalStore((state) => state)
  const [contactUser] = useLocalStorageState<Cruz.User>('contactUser', { defaultValue: _.pick(user, 'email') })
  const [isSent, setIsSent] = useState<boolean>(false)

  const baseUrl = usePathname()
  const querystring = useSearchParams().toString()

  // react-hook-form
  const { control, register, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      rate: 0,
      email: contactUser?.email || '',
      feedback: '',
      base_url: usePathname(),
      querystring: useSearchParams().toString()
    }
  })

  // submit
  const { mutate, isPending } = useMutation({ mutationFn: submit })
  const onSubmit = (data: any) => { mutate(data) }

  async function submit(data: Cruz.Feedback) {
    try {
      data.base_url = baseUrl
      data.querystring = querystring
      data.user_id = user?._id
      data.email = contactUser?.email
      await contactApi.feedback(data)
      setIsSent(true)
      reset()
    } catch (err) {
      return Promise.reject(err)
    }
  }

  function close() {
    setShowFeedback(false)
  }

  useEffect(() => {
    if (!showFeedback) {
      setIsSent(false)
      reset()
    }
  }, [showFeedback])

  const Content = () => {
    if (isSent) {
      return (
        <div className='mt-4'>
          <div className='text-center border px-4 py-6 rounded bg-zinc-50 dark:bg-zinc-800'>
            <div className='text-lg font-semibold'>Feedback Received</div>
            <p className='text-sm mt-2'>
              Thank you for your feedback!<br />
              We appreciate your input and will use it to improve our service.
            </p>
          </div>
          <div className='text-center my-6'>
            <Button type='button' variant='primary' className='rounded-full w-24' onClick={close}>Close</Button>
          </div>
        </div>
      )
    }

    return (
      <form noValidate autoComplete='off' className='w-full mt-2' onSubmit={handleSubmit(onSubmit)}>
        <div className='mb-6'>
          <div className='font-medium text-base'>Rate your experience <span className='font-light text-sm ml-1'>(optional)</span></div>
          <div className='text-sm font-light pt-2 pb-4'>
            * 1 = Very Dissatisfied, 5 = Very Satisfied
          </div>
          <Controller
            name='rate'
            control={control}
            render={({ field: { onChange, value } }) => (
              <RadioGroup value={value} onChange={onChange} className='flex gap-3'>
                {[1, 2, 3, 4, 5].map((num) => (
                  <Radio
                    key={num}
                    value={num}
                    className={({ checked }) =>
                      twMerge(
                        'w-10 h-10 flex items-center justify-center rounded-full border cursor-pointer text-lg font-medium dark:font-normal',
                        checked ? 'bg-zinc-700 text-white' : ' border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-200',
                        'transition-colors duration-150'
                      )
                    }
                    aria-label={`Rate ${num}`}
                  >
                    {num}
                  </Radio>
                ))}
              </RadioGroup>
            )}
          />

          <div className='pt-6'>
            <Textarea
              label={<span className='font-medium text-base/8'>Describe your feedback</span>}
              rows={6} {...register('feedback')}
              data-error={_.get(errors, 'feedback.message')}
            />
          </div>
          <div className='mt-6 text-center'>
            <Button type='submit' variant='primary' className='w-32 rounded-full' working={isPending}>
              Submit
            </Button>
          </div>
        </div>
      </form>
    )
  }

  return (
    <Dialog autoFocus={true} open={showFeedback} onClose={close} className='relative z-20'>
      <DialogBackdrop transition className='dialog-backdrop' />
      <div className='fixed inset-0 z-10 w-full overflow-y-auto p-4'>
        <div className='flex min-h-full items-center justify-center'>
          <DialogPanel className='rounded-lg px-6 py-4 w-[320px] md:w-[480px] sm:px-8 relative bg-white dark:bg-zinc-900 shadow-inner md:shadow-lg'>
            <div className='absolute top-6 right-6'>
              <button type='button' className='rounded-full p-2 -mr-2 hover:bg-zinc-100 dark:bg-zinc-800 transition duration-200'>
                <span className='sr-only'>Dismiss</span>
                <XMarkIcon className='size-6' onClick={close} />
              </button>
            </div>
            <h2>
              Feedback
              <div className='pt-1 border-b-4 border-red-800 w-6' />
            </h2>
            {Content()}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  )
}
